import * as isNumber from 'lodash/isNumber'
import * as sortBy from 'lodash/sortBy'
import * as values from 'lodash/values'
import * as intersection from 'lodash/intersection'
import * as diff from 'lodash/difference'
import * as some from 'lodash/some'
import * as mapValues from 'lodash/mapValues'
import { Timeframe } from 'interfaces/chart'
import { CategoryId } from 'interfaces/meta'
import {
  ExpectedSuccessor,
  Insight,
  InsightCategoriesApiFilter,
  InsightCategoriesFilter,
  InsightCTA,
  InsightCTAKey,
  InsightsOrderState,
  InsightTimeframeFilter,
  LinkCTA,
  PlacementType,
  Successor,
  TrackerCTA,
  InsightTag,
  InsightId,
  InsightCTAFullContainer,
  InsightPlacement,
  PlacementOf,
  InsightCTAContainer,
} from 'interfaces/insights'
import { SocialInnerInsight } from 'interfaces/socialInsights'
import { SettingsField } from 'interfaces/settings'
import { timeperiodToTimeframe, canBeTimeframe } from 'helpers/dateTimeHelpers'
import { AnchorPlacement, Anchor } from 'interfaces/navigation'
import { RelationId } from 'interfaces/peergroups'
import { INSIGHT_PAGES_BY_PLACEMENT } from 'constants/insights'
import { CategorizedPage, OpportunitiesPageType } from 'interfaces/pages'
import { OpportunityPlacement } from 'interfaces/opportunities'
import { OPPORTUNITIES_PAGE_PLACEMENT } from 'constants/opportunities'
import { getItemsFromCollection } from 'helpers/normalizeHelpers'
import { OFFER_PAGE, FEED_POST_PAGE, FEED_AUTHOR_PAGE } from 'constants/pages'
import { getAnchorUrl, getAnchorPage, isCategorizedPage, isTimeframedPage } from 'helpers/pageHelpers'
import { captureWarning, BAD_TIMEPERIOD_INSIGHTS, BAD_CAGEGORY_INSIGHTS } from 'helpers/errorHelpers'
import { resolveCategory } from 'helpers/categoriesHelpers'
import { Collection } from 'interfaces'
import { FloatingPane } from 'interfaces/floatingPanes'

const CTA_WITH_FILTERS = {
  link: true,
  set_tracker: true,
  edit_tracker: true,
} as Record<string, true>

const isCTAWithFilter = (ctaType: string, _cta: InsightCTA): _cta is LinkCTA | TrackerCTA => {
  return !!CTA_WITH_FILTERS[ctaType]
}

const FILTERS_CTA_FIELD_NAME: keyof (LinkCTA | TrackerCTA) = 'filters'
export const hasCTAFilters = (cta: InsightCTA): cta is LinkCTA | TrackerCTA =>
  cta.hasOwnProperty(FILTERS_CTA_FIELD_NAME)

const SUCCESSOR_FIELD_NAME: keyof LinkCTA = 'successor'
export const isLinkCTA = (cta: InsightCTA): cta is LinkCTA =>
  cta.hasOwnProperty(SUCCESSOR_FIELD_NAME)

export const getCTATimeframe = (filter: InsightTimeframeFilter): Timeframe => {
  const { start_date, end_date } = filter

  return timeperiodToTimeframe({ start_date, end_date })
}

const CTA_FILTER_CATEGORY_MAPS: Record<CategorizedPage | OpportunitiesPageType, keyof InsightCategoriesFilter | undefined> = {
  assets: 'asset_category',
  debts: 'debt_category',
  spending: 'spending_category',
  income: 'income_category',
  transactions: 'transactions_category',
  opportunities: 'opportunities_filter',
}
export const getRawCTAFilterCategoryByPage = (filter: InsightCategoriesApiFilter, page: CategorizedPage) => {
  const filterKey = CTA_FILTER_CATEGORY_MAPS[page]
  return filterKey ? filter[filterKey] : undefined
}

const CTA_CATEGORY_STOPS = values(CTA_FILTER_CATEGORY_MAPS)
export const getCTACategory = (filter: InsightCategoriesFilter): CategoryId | null => {
  const existing = CTA_CATEGORY_STOPS.find((stop) => !!stop && filter.hasOwnProperty(stop))

  return existing
    ? (filter[existing] || null)
    : null
}

export const EDIT_CTA_TARGET_MAP: Record<SettingsField, AnchorPlacement> = {
  income: 'settings_income',
}

export const hasAnchorInsights = (anchor: AnchorPlacement): anchor is InsightPlacement =>
  INSIGHT_PAGES_BY_PLACEMENT.hasOwnProperty(anchor)

// if you don't want consider newly loaded insights, pass empty array instead of lastLoadedIds or don't pass it
export const createInsightIdsSorter = (insights: Collection<SocialInnerInsight | Insight, 'id'>, expectedSuccessor: ExpectedSuccessor | null, priorRelationId: RelationId | null) => {
  const { byKey } = insights

  // less has a higher priority
  const isTargetInsight = expectedSuccessor
    ? createIsTargetInsight(expectedSuccessor)
    : () => false

  const successorComparator = (id: InsightId) =>
    isTargetInsight(byKey[id]) ? 0 : 1

  const priorRelationComparator = isNumber(priorRelationId)
    ? (id: number) => byKey[id].context.peergroup_relation === priorRelationId ? 0 : 1
    : 0

  const matchComparator = (id: InsightId) => {
    const { match } = byKey[id]
    return match ? -match.value : 0
  }

  return (ids: InsightId[]) => {
    return sortBy(ids, [successorComparator, priorRelationComparator, matchComparator])
  }
}

export const createIsTargetInsight = (expectedSuccessor: Successor) =>
  (insight: Insight | SocialInnerInsight): boolean => {
    if (expectedSuccessor.family_slug !== insight.family_slug) {
      return false
    }

    if (
      isNumber(expectedSuccessor.peergroup_relation) &&
      expectedSuccessor.peergroup_relation !== insight.context.peergroup_relation
    ) {
      return false
    }

    return true
  }


/* Insights' placement helpers */
export const isOpportunityPlacement = (placement: PlacementType): placement is OpportunityPlacement =>
  placement === OPPORTUNITIES_PAGE_PLACEMENT

export const isHighlightInsight = ({ placement }: Pick<Insight | SocialInnerInsight, 'placement'>) =>
  placement === 'dashboard_opportunities' || placement === 'future_opportunities' || placement === 'coaching_carousel' || placement === 'creditcard_carousel'

export const isCoachingCarousel = ({ placement }: Pick<Insight | SocialInnerInsight, 'placement'>) =>
  placement === 'coaching_carousel'

export const isCreditCardCarousel = ({ placement }: Pick<Insight | SocialInnerInsight, 'placement'>) =>
  placement === 'creditcard_carousel'

export const isOpportunityPageInsight = (insight: Pick<Insight | SocialInnerInsight, 'placement'>): insight is SocialInnerInsight =>
  isOpportunityPlacement(insight.placement)

export const isOpportunityInsight = (insight: Pick<Insight | SocialInnerInsight, 'placement'>) =>
  isOpportunityPageInsight(insight) || isHighlightInsight(insight) || isCreditCardCarousel(insight)

export const isModuleInsight = (insight: Pick<Insight | SocialInnerInsight, 'placement'>) => !isOpportunityInsight(insight)


/* Insights' status helpers */
export const isZeroStateInsight = ({ type }: Pick<Insight, 'type'>) => type === 'zero_state'

export const hasInsightStatus = (status: InsightTag['status'], { tags }: Pick<Insight, 'tags'>) =>
  Boolean(tags && tags.find((tag) => tag.status === status))

export const isInfoInsight = (insight: Pick<Insight, 'tags'>) => hasInsightStatus('info', insight)

export const isSuccessInsight = (insight: Pick<Insight, 'tags'>) => hasInsightStatus('success', insight)

export const isWarningInsight = (insight: Pick<Insight, 'tags'>) => hasInsightStatus('warning', insight)

export const isAlertInsight = (insight: Pick<Insight, 'tags'>) => hasInsightStatus('alert', insight)

export const isServiceInsight = (insight: Pick<Insight, 'tags' | 'type'>) =>
  isWarningInsight(insight) || isAlertInsight(insight) || isZeroStateInsight(insight)


export const isOfferCTA = (type: InsightCTAKey) => type === 'offer'

let fakeInsightId = -1
export const createFakeInsight = <TInsight extends Insight | SocialInnerInsight>(partial: Partial<TInsight> = {}): TInsight => {
  const placement: PlacementType = 'dashboard_spending'
  const id = partial.id || fakeInsightId--

  return {
    id,
    is_warning: false,
    is_dismissed: false,
    is_new: false,
    placement: placement,
    title: `Insight#${ id }`,
    text: `Text of insight#${ id }`,
    tags: [{ status: 'default', text: 'Nice to know' }],

    slug: `${ (partial.placement || placement).toUpperCase() }${ id }`,
    family_slug: `${ (partial.placement || placement).toUpperCase() }${ id }`,
    context: {},
    category: null,
    type: 'insight_without_successor',
    unique_clicks_count: 10,
    socialInsightUid: '',
    ...partial,
  } as any as TInsight
}

export function dismissInsight (insight: Insight | SocialInnerInsight, state: InsightsOrderState): InsightsOrderState {
  const placementIds = state.byPlacement[insight.placement]
  if (!placementIds) {
    return state
  }

  return {
    ...state,
    byPlacement: mapValues(
      state.byPlacement,
      (ids) => ids
        ? ids.filter((id) => id !== insight.id)
        : []
    ),
  }
}

export function undismissInsight (insight: Insight | SocialInnerInsight, state: InsightsOrderState): InsightsOrderState {
  const { placement, id } = insight
  const placementIds = state.byPlacement[placement]
  if (!placementIds) {
    return state
  }

  const ids = placementIds.concat(id)
  const nextByPlacement = {
    ...state.byPlacement,
    [placement]: ids,
  }

  return {
    ...state,
    byPlacement: nextByPlacement,
  }
}

export const createInsightGenerator = <TInsight extends Insight | SocialInnerInsight>(placement: PlacementOf<TInsight>) => {
  let lastId = 0
  return (partial: Partial<TInsight> = {}): TInsight =>
    createFakeInsight<TInsight>({ id: ++lastId, placement, ...partial })
}
export const createInsightGroupKey = <TPlacement>(placement: TPlacement) => placement

export const getInsightsPlacements = <TInsight extends Insight | SocialInnerInsight>(insights: TInsight[] | Collection<TInsight, 'id'>): PlacementOf<TInsight>[] => {
  const insightArray = 'byKey' in insights ? getItemsFromCollection(insights) : insights

  return Array.from(
    insightArray.reduce(
      (acc, { placement }) => acc.add(placement as PlacementOf<TInsight>),
      new Set<PlacementOf<TInsight>>()
    ),
  )
}

const LINK_ANCHOR = 'anchor' as const
const LINK_URL = 'url' as const
const LINK_NONE = 'none' as const
const LINK_POPUP = 'popup' as const

type LinkGeneric<TLinkType, TToType> = {
  to: TToType,
  url: string,
  type: TLinkType
}

type LinkAnchor = LinkGeneric<typeof LINK_ANCHOR, Anchor>
type LinkUrl = LinkGeneric<typeof LINK_URL, string> & { search?: Record<any, any> }
type LinkEmpty = LinkGeneric<typeof LINK_NONE, null>
type LinkPopup = LinkGeneric<typeof LINK_POPUP, FloatingPane['type']>

type Link =
  | LinkAnchor
  | LinkUrl
  | LinkEmpty
  | LinkPopup

function getLink (to: Anchor): LinkAnchor {
  return {
    to,
    url: getAnchorUrl(to),
    type: LINK_ANCHOR,
  }
}

export function getInsightLinkTo (cta: Partial<InsightCTAFullContainer>): Link {
  if (cta.link) {
    return getLink(cta.link.target)
  }

  if (cta.meeting) {
    const { uid, token } = cta.meeting
    return {
      to: 'video',
      url: 'video',
      type: LINK_URL,
      search: {
        meeting_uid: uid,
        ...token ? { token } : {},
      },
    }
  }

  if (cta.meeting_info) {
    return {
      to: 'advisor',
      url: '',
      type: LINK_POPUP,
    }
  }

  if (cta.coach_review) {
    const { meeting_uid, token, rating } = cta.coach_review
    return {
      to: '/coaching/review',
      url: '/coaching/review',
      search: {
        meeting_uid,
        ...token ? { token } : {},
        ...rating ? { rating } : {},
      },
      type: LINK_URL,
    }
  }

  if (cta.link_creditfile) {
    return getLink('credit-linkage')
  }

  if (cta.set_tracker) {
    return getLink(cta.set_tracker.target)
  }

  if (cta.edit_tracker) {
    return getLink(cta.edit_tracker.target)
  }

  if (cta.edit) {
    return getLink(EDIT_CTA_TARGET_MAP[cta.edit.field])
  }

  if (cta.add_account) {
    return {
      to: 'provider-search',
      url: '',
      type: LINK_POPUP,
    }
  }

  if (cta.add_car) {
    return {
      to: 'car-edit',
      url: '',
      type: LINK_POPUP,
    }
  }

  if (cta.add_home) {
    return {
      to: 'house-edit',
      url: '',
      type: LINK_POPUP,
    }
  }

  if (cta.update_credentials) {
    return getLink('linkedaccounts')
  }

  if (cta.view_linked_offer) {
    return getLink({ anchor: OFFER_PAGE, id: cta.view_linked_offer.offer_id })
  }

  if (cta.offer) {
    return {
      to: cta.offer.url,
      url: cta.offer.url,
      type: LINK_URL,
    }
  }

  if (cta.view_post) {
    return getLink({ anchor: FEED_POST_PAGE, id: cta.view_post.post_uid })
  }

  if (cta.view_comment) {
    return getLink({ anchor: FEED_POST_PAGE, id: cta.view_comment.post_uid })
  }

  if (cta.view_social_profile) {
    return getLink({ anchor: FEED_AUTHOR_PAGE, id: cta.view_social_profile.social_user_uid })
  }

  if (cta.popup) {
    return {
      to: cta.popup.type,
      url: '',
      type: LINK_POPUP,
    }
  }

  return {
    to: null,
    url: '',
    type: LINK_NONE,
  }
}

/*
 * Forbidden CTAs may appear when we remove some controls from the UI, etc.
 */
export const filterForbiddenCTAs = <TInsight extends Insight | SocialInnerInsight>(insights: TInsight[]): TInsight[] => {
  return insights.map<TInsight>((insight) => {
    const { cta, ...restInsight } = insight
    if (cta && cta.link && ['networth_inflows', 'networth_outflows'].includes(cta.link.target)) {
      const { link, ...restCTA } = cta

      return {
        ...restInsight,
        cta: restCTA,
      } as TInsight
    }

    return insight
  })
}

/**
 * Reject bad insights (with incorrect timeperiods)
 * Generally it's not good but @aimestereo recommended to insure
 */
const filterBadTimeframeInsights = <TInsight extends Insight | SocialInnerInsight>(insights: TInsight[]): TInsight[] => {
  const badInsights = insights
    .filter((insight) => Boolean(insight.cta))
    .filter(({ cta }) => {
      if (!cta) {
        return false
      }

      return some(cta, (action, ctaType) => {
        if (!action || !ctaType) {
          return false
        }

        if (!isCTAWithFilter(ctaType, action)) {
          return false
        }

        const page = getAnchorPage(action.target)
        if (!page || !isTimeframedPage(page)) {
          return false
        }

        // having no timeframe is invalid case
        const { filters } = action

        if (!filters) {
          return false
        }

        if (!filters.start_date || !filters.end_date) {
          return true
        }

        const timeperiod = {
          start_date: filters.start_date,
          end_date: filters.end_date,
        }

        // true - means rejected & can't be timeframe
        return !canBeTimeframe(timeperiod)
      })
    })

  if (badInsights.length > 0) {
    captureWarning(BAD_TIMEPERIOD_INSIGHTS, {
      insights: badInsights,
    })
  }
  const goodInsights = diff(insights, badInsights)

  return goodInsights
}

/**
 * Reject bad insights (that required categoryId in cta, but it's missed)
 * Generally it's not good but @aimestereo recommended to insure
 */
const filterMissedCategoryInsights = <TInsight extends Insight | SocialInnerInsight>(insights: TInsight[]): TInsight[] => {
  const badInsights = insights
    .filter((insight) => Boolean(insight.cta))
    .filter(({ cta }) => {
      if (!cta) {
        return false
      }

      return some(cta, (action, ctaType) => {
        if (!action || !ctaType) {
          return false
        }

        if (!isCTAWithFilter(ctaType, action)) {
          return false
        }

        // having no category is invalid case
        const page = getAnchorPage(action.target)
        if (!page || !isCategorizedPage(page)) {
          return false
        }

        const { filters } = action
        const category = getRawCTAFilterCategoryByPage(filters, page)

        return category === undefined
      })
    })

  if (badInsights.length > 0) {
    captureWarning(BAD_CAGEGORY_INSIGHTS, {
      insights: badInsights,
    })
  }
  const goodInsights = diff(insights, badInsights)

  return goodInsights
}

// reject insights with bad cta
export const filterBadInsights = <TInsight extends Insight | SocialInnerInsight>(insights: TInsight[]): TInsight[] => {
  const insightsWithGoodTimeframe = filterBadTimeframeInsights(insights)
  const insightsWithCategory = filterMissedCategoryInsights(insights)

  return intersection(insightsWithGoodTimeframe, insightsWithCategory)
}


export const convertCTAFilterCategories = <TInsight extends Insight | SocialInnerInsight>(insights: TInsight[]): TInsight[] => {
  return insights.map((insight) => {
    if (!insight.cta) {
      return insight
    }

    return {
      ...insight,
      cta: mapValues(insight.cta, (cta) => {
        if (!cta) {
          return cta
        }

        if (!hasCTAFilters(cta)) {
          return cta
        }

        const nextFilters = { ...cta.filters }
        if (typeof nextFilters.asset_category !== 'undefined') {
          nextFilters.asset_category = resolveCategory(nextFilters.asset_category, false)
        }
        if (typeof nextFilters.debt_category !== 'undefined') {
          nextFilters.debt_category = resolveCategory(nextFilters.debt_category, false)
        }
        if (typeof nextFilters.income_category !== 'undefined') {
          nextFilters.income_category = resolveCategory(nextFilters.income_category, false)
        }
        if (typeof nextFilters.spending_category !== 'undefined') {
          nextFilters.spending_category = resolveCategory(nextFilters.spending_category, false)
        }

        return {
          ...cta,
          filters: nextFilters,
        }
      }),
    }
  })
}

export const parseCta = (text: string, decode: boolean): InsightCTAContainer | null => {
  let cta = new URLSearchParams(text).get('cta')
  if (!cta) {
    return null
  }

  try {
    if (decode) {
      cta = decodeURIComponent(cta)
    }
    return JSON.parse(cta)
  } catch (e) {}
  return null
}
